import { Box, Flex, Text as ChakraText } from '@chakra-ui/react';
import type {
  RelatedDeliveryOrder,
  RelatedDeliveryOrdersResponse,
} from '@diamond/shared/types';
import { GridTable, Text } from '@diamond/shared/ui';
import { formatCurrencyIdr, unixToHumanDate } from '@diamond/shared/utils';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';

export interface RelatedDeliveryOrdersProps {
  summary: RelatedDeliveryOrdersResponse['summary'];
  items: RelatedDeliveryOrder[];
  isLoading: boolean;
  isPlaceholder?: boolean;
}

interface RelatedDeliveryOrderRowData {
  deliveryOrderNo: string;
  invoiceNo: string;
  deliveryDate: string;
  shipTo: string;
  outletName: string;
  plantName: string;
  invoiceAmount: string;
}

function mapRelatedItem(
  item: RelatedDeliveryOrder
): RelatedDeliveryOrderRowData {
  return {
    deliveryOrderNo: item.delivery_order_number || '-',
    invoiceNo: item.invoice_no || '-',
    deliveryDate: item.delivery_date
      ? unixToHumanDate(item.delivery_date, 'd MMM yy')
      : '-',
    shipTo: item.sap_no_shipto ? String(item.sap_no_shipto) : '-',
    outletName: item.outlet_name || '-',
    plantName:
      (item as RelatedDeliveryOrder & { plant_name?: string }).plant_name ||
      '-',
    invoiceAmount: formatCurrencyIdr(item.invoice_amount),
  };
}

export function RelatedDeliveryOrders({
  summary,
  items,
  isLoading,
  isPlaceholder = false,
}: RelatedDeliveryOrdersProps) {
  const rows = useMemo(() => items.map(mapRelatedItem), [items]);
  const hasRelatedRows = rows.length > 0;
  const hasRelatedCount = !isPlaceholder && summary.related_count > 0;
  const summaryValueColor = isPlaceholder ? '#AEAEB2' : '#373F41';
  const poValue = isPlaceholder ? '-' : summary.po_number || '-';
  const nilaiPoValue = isPlaceholder
    ? '-'
    : formatCurrencyIdr(summary.total_po_amount);

  const columnDefs = useMemo<ColDef<RelatedDeliveryOrderRowData>[]>(
    () => [
      {
        field: 'deliveryOrderNo',
        headerName: 'Nomor SJ',
        width: 140,
        tooltipField: 'deliveryOrderNo',
      },
      {
        field: 'invoiceNo',
        headerName: 'No. Faktur',
        width: 140,
        tooltipField: 'invoiceNo',
      },
      { field: 'deliveryDate', headerName: 'Tgl. SJ', width: 75 },
      { field: 'shipTo', headerName: 'No. Ship to', width: 90 },
      {
        field: 'outletName',
        headerName: 'Nama Outlet',
        width: 140,
        tooltipField: 'outletName',
      },
      {
        field: 'plantName',
        headerName: 'Cabang/Plant',
        width: 110,
        tooltipField: 'plantName',
      },
      {
        field: 'invoiceAmount',
        headerName: 'Nilai Faktur',
        minWidth: 100,
        flex: 1,
      },
    ],
    []
  );

  const rowHeight = 42;
  const headerHeight = 48;
  const maxTableHeight = headerHeight + 5 * rowHeight;

  const tableHeight = rows.length
    ? `${Math.min(
        headerHeight + rows.length * rowHeight + 2,
        maxTableHeight
      )}px`
    : '46px';

  return (
    <Box mt={6}>
      {hasRelatedRows ? (
        <Flex justify="space-between" align="flex-start" mb={1}>
          <Box>
            <Flex gap={8} align="center">
              <Text
                fontWeight={500}
                fontSize="20px"
                lineHeight="100%"
                letterSpacing="0%"
                color={summaryValueColor}
              >
                PO: {poValue}
              </Text>
              {hasRelatedCount ? (
                <Text
                  fontWeight={500}
                  fontSize="20px"
                  lineHeight="100%"
                  letterSpacing="0%"
                  color="#373F41"
                >
                  {summary.related_count} Surat Jalan Terkait
                </Text>
              ) : null}
              <Text
                fontWeight={500}
                fontSize="20px"
                lineHeight="100%"
                letterSpacing="0%"
                color={summaryValueColor}
              >
                Nilai PO: {nilaiPoValue}
              </Text>
            </Flex>
            <ChakraText
              fontWeight={400}
              fontSize="12px"
              lineHeight="100%"
              letterSpacing="0%"
              color="#8E8E93"
              mt={1}
            >
              Surat jalan yang ditemukan terhubung dengan surat jalan lainnya
              melalui purchase order yang sama
            </ChakraText>
          </Box>
        </Flex>
      ) : (
        <Flex width="100%" justify="space-between" align="center" mb={1}>
          <Text
            fontWeight={500}
            fontSize="20px"
            lineHeight="100%"
            letterSpacing="0%"
            color={summaryValueColor}
          >
            PO: {poValue}
          </Text>
          {hasRelatedCount ? (
            <Text
              fontWeight={500}
              fontSize="20px"
              lineHeight="100%"
              letterSpacing="0%"
              color="#373F41"
            >
              {summary.related_count} Surat Jalan Terkait
            </Text>
          ) : null}
          <Text
            fontWeight={500}
            fontSize="20px"
            lineHeight="100%"
            letterSpacing="0%"
            color={summaryValueColor}
          >
            Nilai PO: {nilaiPoValue}
          </Text>
        </Flex>
      )}

      {hasRelatedRows ? (
        <Box
          mt={3}
          sx={{
            '& .ag-theme-diamond .ag-header-cell-text': {
              fontWeight: '500 !important',
              fontSize: '10px !important',
              color: '#373F41 !important',
            },
          }}
        >
          <GridTable<RelatedDeliveryOrderRowData>
            columnDefs={columnDefs}
            rowData={rows}
            pagination={false}
            isLoading={isLoading}
            height={tableHeight}
            minHeight={tableHeight}
            gridOptions={{
              suppressNoRowsOverlay: true,
              defaultColDef: {
                sortable: false,
                resizable: false,
              },
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
